import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import { useFormInstruction } from "Constant/apiHooks";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { LoadingComponent } from "Components";
import { unAuthService } from "Service/Axios";
import Cookie from "Service/Cookie";
import { AUTH_TOKEN } from "Constant/constants";
import { NO_AUTH_FORM_BUILDER_DATE_VALIDATE_URL } from "Constant/api";
import { formatDateLocal } from "Utils";
import { paths } from "Constant/paths";

/** ---------- SEO helpers (no backend changes needed) ---------- */
const stripHtml = (html = "") =>
  html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const truncate = (s = "", n = 170) =>
  s.length > n ? s.slice(0, n - 1).trim() + "…" : s;

const cleanUrl = (u = "") => (u || "").trim();

const toISO = (ms) => (ms ? new Date(ms).toISOString() : null);

const isJobLike = (name = "") =>
  /(postdoc|postdoctoral|faculty|professor|researcher|phd|job|position|hiring|vacancy)/i.test(
    name
  );

const isBotUA = () => {
  if (typeof navigator === "undefined") return false;
  return /bot|crawler|spider|crawling|googlebot|bingbot|yandex/i.test(
    navigator.userAgent
  );
};

// Clean up common junk in URLs (like trailing newlines/spaces)
const normalizeImageUrl = (u) => {
  const url = cleanUrl(u);
  return url.replace(/\s+/g, "");
};

// Best-effort country detection from the Location text
const detectCountryCode = (locationText = "") => {
  const t = (locationText || "").toLowerCase();
  if (t.includes("saudi")) return "SA";
  if (t.includes("uae")) return "AE";
  if (t.includes("qatar")) return "QA";
  if (t.includes("bahrain")) return "BH";
  if (t.includes("kuwait")) return "KW";
  if (t.includes("oman")) return "OM";
  if (t.includes("singapore")) return "SG";
  if (t.includes("india")) return "IN";
  if (t.includes("china")) return "CN";
  if (t.includes("usa") || t.includes("united states")) return "US";
  if (t.includes("uk") || t.includes("united kingdom")) return "GB";
  return undefined;
};

// Extract common fields from welcome text WITHOUT changing API structure
const extractFromWelcomeText = (welcomeHtml = "") => {
  const text = stripHtml(welcomeHtml);

  // Best-effort: capture "Location: ..." OR "📍 ..."
  const locationMatch =
    text.match(/Location:\s*([^\n\r.]+)/i) ||
    text.match(/📍\s*([^\n\r.]+)/i);

  // Best-effort org extraction:
  // - Try KAUST (your known case)
  // - Else try to find a line containing "University", "Institute", "College", "School"
  let orgName = null;

  if (
    /King Abdullah University of Science and Technology/i.test(text) ||
    /\bKAUST\b/i.test(text)
  ) {
    orgName = "King Abdullah University of Science and Technology (KAUST)";
  } else {
    // Try grabbing a likely org phrase from the text
    const orgMatch =
      text.match(/([A-Z][A-Za-z&().,\- ]{2,80}\bUniversity\b[A-Za-z&().,\- ]{0,80})/) ||
      text.match(/([A-Z][A-Za-z&().,\- ]{2,80}\bInstitute\b[A-Za-z&().,\- ]{0,80})/) ||
      text.match(/([A-Z][A-Za-z&().,\- ]{2,80}\bCollege\b[A-Za-z&().,\- ]{0,80})/) ||
      text.match(/([A-Z][A-Za-z&().,\- ]{2,80}\bSchool\b[A-Za-z&().,\- ]{0,80})/);

    if (orgMatch && orgMatch[1]) orgName = orgMatch[1].trim();
  }

  return {
    orgName,
    locationText: locationMatch ? locationMatch[1].trim() : null,
    plainText: text,
  };
};

// Better meta description than "first 170 chars"
const buildBestDescription = (welcomeHtml = "") => {
  const text = stripHtml(welcomeHtml);
  if (!text) return "";

  // Prefer content after "Position Overview"
  const idx = text.toLowerCase().indexOf("position overview");
  if (idx !== -1) {
    const snippet = text.slice(idx + "position overview".length).trim();
    return truncate(snippet, 170);
  }

  // Else prefer first ~2 sentences
  const sentences = text.split(/(?<=[.!?])\s+/).filter(Boolean);
  const firstTwo = sentences.slice(0, 2).join(" ");
  return truncate(firstTwo || text, 170);
};

const FormInstruction = () => {
  const { t } = useTranslation();
  const {
    formBuilder: { applicationForm },
    registration,
    guestApplication,
    formInstruction: formInstructionRoute,
  } = paths;

  const { formId } = useParams();
  const navigate = useNavigate();
  const { search } = useLocation();
  const searchParams = search?.split("?")?.[1];

  const { formInstruction, loading: formInstructionLoading } =
    useFormInstruction(formId, searchParams);

  const onRedirect = () => {
    if (formInstruction?.loginRequired) {
      const Token = Cookie.getCookie(AUTH_TOKEN);
      if (Token) {
        navigate(`${applicationForm}/${formId}${search}`);
      } else {
        localStorage.setItem(
          "isFormAplication",
          `${formInstructionRoute}/${formId}${search}`
        );
        navigate(registration);
      }
    } else {
      navigate(`${guestApplication}/${formId}${search}`);
    }
  };

  // IMPORTANT for SEO: don't auto-redirect bots.
  useEffect(() => {
    if (
      !formInstructionLoading &&
      formInstruction &&
      !formInstruction?.instructionRequired
    ) {
      if (!isBotUA()) onRedirect();
    }
  }, [formInstructionLoading, formInstruction]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkDateValidation = async () => {
    const payload = {
      formId: Number(formId),
      date: formatDateLocal(`${new Date()}`),
    };

    const response = await unAuthService(
      NO_AUTH_FORM_BUILDER_DATE_VALIDATE_URL,
      "POST",
      payload
    );

    if (response && response.message === "Form is valid") {
      onRedirect();
    } else if (response && response.message === "The form has expired.") {
      toast.error(
        t("common.messages.notification.failed.formExpired", {
          url: process.env.REACT_APP_DEV_URL,
        })
      );
    } else {
      toast.error(t("common.messages.notification.failed.somethingWentWrong"));
    }
  };

  /** ---------- Build dynamic SEO from API JSON ---------- */
  const seo = useMemo(() => {
    const canonical = formInstruction?.formId
      ? `https://www.plutoedu.com/form/${formInstruction.formId}`
      : `https://www.plutoedu.com/form/${formId}`;

    const rawTitle = formInstruction?.formName || "PlutoEdu";
    const seoTitle = formInstruction?.formName
      ? `${rawTitle} | PlutoEdu`
      : "PlutoEdu";

    const welcomeHtml = formInstruction?.formSetting?.welcomeText || "";
    const extracted = extractFromWelcomeText(welcomeHtml);

    const seoDescription =
      buildBestDescription(welcomeHtml) ||
      "Apply via PlutoEdu. View details and submit your application.";

    const seoImage =
      normalizeImageUrl(formInstruction?.coverImage) ||
      normalizeImageUrl(formInstruction?.formSetting?.logoURL) ||
      normalizeImageUrl(formInstruction?.formSetting?.companyLogoUrl) ||
      "https://www.plutoedu.com/assets/og/postdoc-default.jpg";

    const postedISO = toISO(formInstruction?.formSetting?.startForm);
    const validISO = toISO(formInstruction?.formSetting?.endForm);

    const addJobSchema = isJobLike(formInstruction?.formName || "");
    const countryCode = detectCountryCode(extracted.locationText || "");

    const jobSchema =
      formInstruction && addJobSchema
        ? (() => {
            const schema = {
              "@context": "https://schema.org",
              "@type": "JobPosting",
              title: formInstruction.formName,
              description: extracted.plainText.slice(0, 6000),
              datePosted: postedISO || undefined,
              validThrough: validISO || undefined,
              employmentType: "FULL_TIME",
              hiringOrganization: {
                "@type": "Organization",
                name: extracted.orgName || "Hiring Organization",
                logo:
                  normalizeImageUrl(
                    formInstruction?.formSetting?.companyLogoUrl
                  ) ||
                  normalizeImageUrl(formInstruction?.formSetting?.logoURL) ||
                  seoImage,
              },
              jobLocation: extracted.locationText
                ? {
                    "@type": "Place",
                    address: {
                      "@type": "PostalAddress",
                      addressLocality: extracted.locationText,
                      addressCountry: countryCode || undefined,
                    },
                  }
                : undefined,
              identifier: {
                "@type": "PropertyValue",
                name: "PlutoEdu",
                value: String(formInstruction.formId),
              },
              directApply: true,
              url: canonical,
            };

            // Remove undefined keys for clean JSON-LD
            Object.keys(schema).forEach(
              (k) => schema[k] === undefined && delete schema[k]
            );
            return schema;
          })()
        : null;

    return {
      canonical,
      seoTitle,
      seoDescription,
      seoImage,
      jobSchema,
    };
  }, [formInstruction, formId]);

  // Safe link for crawlers (do NOT invent /apply if it doesn’t exist)
  const applyHref = seo.canonical;

  return (
    <div className="bg-white">
      <Helmet prioritizeSeoTags>
        {!formInstruction ? (
          <>
            <title>PlutoEdu</title>
            <meta key="robots" name="robots" content="noindex,follow" />
            <meta
              key="desc"
              name="description"
              content="Apply via PlutoEdu."
            />
          </>
        ) : (
          <>
            <title>{seo.seoTitle}</title>

            <meta
              key="desc"
              name="description"
              content={seo.seoDescription}
            />
            <link key="canonical" rel="canonical" href={seo.canonical} />
            <meta
              key="robots"
              name="robots"
              content="index,follow,max-image-preview:large"
            />

            {/* OpenGraph */}
            <meta key="og:site_name" property="og:site_name" content="PlutoEdu" />
            <meta key="og:locale" property="og:locale" content="en_US" />
            <meta key="og:title" property="og:title" content={seo.seoTitle} />
            <meta
              key="og:desc"
              property="og:description"
              content={seo.seoDescription}
            />
            <meta key="og:type" property="og:type" content="article" />
            <meta key="og:url" property="og:url" content={seo.canonical} />
            <meta key="og:image" property="og:image" content={seo.seoImage} />

            {/* Twitter */}
            <meta key="tw:card" name="twitter:card" content="summary_large_image" />
            <meta key="tw:site" name="twitter:site" content="@PlutoEdu" />
            <meta key="tw:title" name="twitter:title" content={seo.seoTitle} />
            <meta
              key="tw:desc"
              name="twitter:description"
              content={seo.seoDescription}
            />
            <meta key="tw:image" name="twitter:image" content={seo.seoImage} />
            <meta key="tw:url" name="twitter:url" content={seo.canonical} />

            {/* DEBUG – remove after verification */}
            <meta key="helmet-check" name="x-helmet-check" content="meta-working" />

            {/* ✅ JobPosting JSON-LD (make it stable with a key) */}
            {seo.jobSchema ? (
              <script
                key={`ldjson-${formId}`}
                type="application/ld+json"
              >
                {JSON.stringify(seo.jobSchema)}
              </script>
            ) : null}
          </>
        )}
      </Helmet>

      <LoadingComponent isLoading={formInstructionLoading}>
        {formInstruction && formInstruction?.formSetting && (
          <>
            {formInstruction.formSetting.headerRequired && (
              <header
                className="py-3 text-white position-sticky top-0"
                style={{
                  backgroundColor:
                    formInstruction.formSetting.headerBackgroundColor ||
                    "#182A58",
                }}
              >
                <div className="container-fluid">
                  <div className="d-flex flex-wrap row-gap-2 justify-sm-content-between justify-content-center">
                    <div className="ins-logo me-5">
                      <img
                        src={normalizeImageUrl(
                          formInstruction.formSetting.companyLogoUrl
                        )}
                        alt=""
                        className="img-fluid"
                      />
                    </div>

                    {formInstruction.formSetting.buttonRequired && (
                      <div className="ms-sm-auto text-center">
                        <span className="me-2">
                          {formInstruction.formSetting.buttonSubText}
                        </span>
                        <button
                          className="btn btn-secondary"
                          onClick={checkDateValidation}
                        >
                          {formInstruction.formSetting.buttonName}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </header>
            )}

            <div className="container-fluid py-3">
              <div className="">
                <div className="display-view preview-img">
                  {formInstruction?.formSetting?.banner ? (
                    <div className="for-banner mb-4">
                      {formInstruction?.formSetting?.logoURL && (
                        <img
                          src={normalizeImageUrl(
                            formInstruction?.formSetting?.logoURL
                          )}
                          alt="banner"
                        />
                      )}
                    </div>
                  ) : (
                    <div className="for-logo mb-4">
                      {formInstruction?.formSetting?.logoURL && (
                        <img
                          src={normalizeImageUrl(
                            formInstruction?.formSetting?.logoURL
                          )}
                          alt="logo"
                        />
                      )}
                    </div>
                  )}
                </div>

                <div className="text-start">
                  <div className="pb-2 border-bottom">
                    <h3 className="text-center">
                      <strong>{formInstruction.formName}</strong>
                    </h3>
                  </div>

                  <div className="pt-3 pb-4 p-nogap">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: formInstruction?.formSetting?.welcomeText,
                      }}
                    />

                    <div className="pt-3 border-top">
                      <a
                        className="btn btn-primary"
                        href={applyHref}
                        onClick={(e) => {
                          e.preventDefault();
                          checkDateValidation();
                        }}
                      >
                        Apply Now
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <footer className="py-3 bg-slate-300">
              <div className="container">
                <div className="text-center">
                  &copy; {`${new Date().getFullYear()}`} Campustask Software Tech
                  Solutions Private Limited. All rights reserved.
                </div>
              </div>
            </footer>
          </>
        )}
      </LoadingComponent>
    </div>
  );
};

export default FormInstruction;
